import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { tenantDashboard, tenantBranding, updateBranding, listMembers, teamInvite, removeMember, billingSummary, billingSubscribe, billingBuyCredits, billingPortal, tenantEmbed, updateEmbed, tenantLeads, updateLeadStatus, gettingStarted, trackActivation, tenantAnalytics, updateNotifications } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { track } from "../../lib/analytics";
import { RequireAuth } from "./guards";
import { Shell, Card, H1, Field, Btn, Label, Input } from "./ui";

const TABS = ["Overview", "Analytics", "Leads", "Embed", "Billing", "Branding", "Team"];

function Analytics() {
  const [d, setD] = useState(null);
  useEffect(() => { tenantAnalytics().then(setD).catch(() => {}); }, []);
  if (!d) return <p className="text-white/40">Loading…</p>;
  const money = (n) => (n == null ? "—" : `£${Number(n).toFixed(2)}`);
  const money0 = (n) => (n == null ? "£0" : `£${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`);
  const cards = [
    ["Designer visits", d.designer_visits], ["Designs created", d.designs_created], ["Leads generated", d.leads],
    ["Lead conversion", d.lead_conversion_rate == null ? "—" : `${d.lead_conversion_rate}%`], ["Avg lead value", money(d.avg_lead_value ?? d.avg_quote_value)],
    ["Won conversion", d.conversion_rate == null ? "—" : `${d.conversion_rate}%`], ["Avg time to close", d.avg_days_to_win == null ? "—" : `${d.avg_days_to_win} days`],
  ];
  return (
    <div className="space-y-6" data-testid="analytics-tab">
      <div className="grid md:grid-cols-3 gap-4" data-testid="roi-panel">
        <Card className="border-[#00D26A]/40"><div className="text-white/40 text-xs uppercase">Pipeline value</div><div className="text-3xl font-black text-[#00D26A] mt-1" data-testid="pipeline-value">{money0(d.pipeline_value)}</div><div className="text-xs text-white/40 mt-1">Estimated £ in open enquiries (new / contacted / quoted)</div></Card>
        <Card><div className="text-white/40 text-xs uppercase">Won value</div><div className="text-3xl font-black mt-1" data-testid="won-value">{money0(d.won_value)}</div><div className="text-xs text-white/40 mt-1">Total value of projects marked won</div></Card>
        <Card><div className="text-white/40 text-xs uppercase">Total value generated</div><div className="text-3xl font-black mt-1" data-testid="total-value">{money0(d.total_lead_value)}</div><div className="text-xs text-white/40 mt-1">All quote value your designer has produced</div></Card>
      </div>
      <label className="flex items-center gap-2 text-sm text-white/70" data-testid="roi-email-toggle">
        <input type="checkbox" defaultChecked={d.roi_email_enabled !== false} onChange={(e) => updateNotifications({ roi_email: e.target.checked }).then(() => toast.success("Saved")).catch(() => toast.error("Could not save"))} className="accent-[#00D26A]" />
        Email me a monthly ROI summary
      </label>
      <div className="grid md:grid-cols-5 gap-4">
        {cards.map(([l, v]) => (
          <Card key={l}><div className="text-2xl font-bold">{v}</div><div className="text-white/40 text-xs uppercase mt-1">{l}</div></Card>
        ))}
      </div>
      <div className="grid md:grid-cols-2 gap-6">
        <Card><h3 className="font-semibold mb-3">Top turf sizes requested</h3>
          {d.top_turf_sizes.length === 0 ? <p className="text-white/40 text-sm">No leads yet.</p> :
            <div className="space-y-2">{d.top_turf_sizes.map((s) => (<div key={s.size} className="flex justify-between text-sm border-b border-white/5 py-1"><span>{s.size}</span><span className="text-white/50">{s.count}</span></div>))}</div>}
        </Card>
        <Card><h3 className="font-semibold mb-3">Lead status breakdown</h3>
          {Object.keys(d.status_breakdown).length === 0 ? <p className="text-white/40 text-sm">No leads yet.</p> :
            <div className="space-y-2">{Object.entries(d.status_breakdown).map(([k, v]) => (<div key={k} className="flex justify-between text-sm border-b border-white/5 py-1"><span className="capitalize">{k}</span><span className="text-white/50">{v}</span></div>))}</div>}
        </Card>
      </div>
    </div>
  );
}

function Overview({ data }) {
  const p = data.plan || {}; const ai = data.ai_usage || {};
  const lim = ai.limit >= 1000000 ? "∞" : ai.limit;
  const [gs, setGs] = useState(null);
  const [an, setAn] = useState(null);
  useEffect(() => { gettingStarted().then(setGs).catch(() => {}); }, []);
  useEffect(() => { tenantAnalytics().then(setAn).catch(() => {}); }, []);
  const gbp0 = (n) => `£${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  return (
    <div className="space-y-6">
    {an && (
      <Card className="border-[#00D26A]/40" data-testid="overview-pipeline">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="text-white/40 text-xs uppercase tracking-wider">Pipeline value</div>
            <div className="text-4xl font-black text-[#00D26A] mt-1" data-testid="overview-pipeline-value">{gbp0(an.pipeline_value)}</div>
            <div className="text-xs text-white/40 mt-1">Estimated £ sitting in your open enquiries</div>
          </div>
          <div className="flex gap-8 text-right">
            <div><div className="text-white/40 text-[11px] uppercase">Leads</div><div className="text-2xl font-bold mt-1">{an.leads}</div></div>
            <div><div className="text-white/40 text-[11px] uppercase">Won value</div><div className="text-2xl font-bold mt-1">{gbp0(an.won_value)}</div></div>
            <div><div className="text-white/40 text-[11px] uppercase">Total generated</div><div className="text-2xl font-bold mt-1">{gbp0(an.total_lead_value)}</div></div>
          </div>
        </div>
      </Card>
    )}
    <div className="grid md:grid-cols-3 gap-6" data-testid="dash-overview">
      <Card><div className="text-white/40 text-xs uppercase">Company</div><div className="text-xl font-semibold mt-1">{data.tenant.name}</div><div className="text-xs text-white/40 mt-1">Role: {data.role}</div></Card>
      <Card><div className="text-white/40 text-xs uppercase">Plan</div><div className="text-xl font-semibold mt-1 capitalize">{(p.plan || "no plan").replace(/_/g, " ")}</div><div className="text-xs text-white/40 mt-1">{p.status || "not subscribed"}</div></Card>
      <Card><div className="text-white/40 text-xs uppercase">AI usage</div><div className="text-xl font-semibold mt-1">{ai.used} / {lim}</div><div className="text-xs text-white/40 mt-1">generations this period</div></Card>
    </div>
    {gs && gs.completed < gs.total && (
      <Card data-testid="getting-started">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold">Setup Progress</h3>
          <span className="text-xs text-[#00D26A] font-semibold">Your designer is {gs.percent}% ready</span>
        </div>
        <div className="h-1.5 bg-white/10 rounded-full mb-4"><div className="h-full bg-[#00D26A] rounded-full transition-all" style={{ width: `${gs.percent}%` }} /></div>
        <div className="space-y-2">
          {gs.steps.map((s) => (
            <div key={s.key} className="flex items-center gap-3 text-sm" data-testid={`gs-${s.key}`}>
              <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[11px] ${s.done ? "bg-[#00D26A] text-black" : "border border-white/25 text-white/30"}`}>{s.done ? "✓" : ""}</span>
              <span className={s.done ? "text-white/50 line-through" : "text-white/80"}>{s.label}</span>
            </div>
          ))}
        </div>
      </Card>
    )}
    <div className="grid md:grid-cols-3 gap-6">
      <Card className="md:col-span-2"><h3 className="font-semibold mb-3">Recent designs</h3>
        {(data.recent_designs || []).length === 0 ? <p className="text-white/40 text-sm">No designs yet.</p> :
          <div className="space-y-1 text-sm">{data.recent_designs.map((d) => <div key={d.design_id} className="flex justify-between border-b border-white/5 py-1"><span>{d.name || d.design_id}</span><span className="text-white/40">{(d.created_at || "").slice(0, 10)}</span></div>)}</div>}
      </Card>
      <Card><h3 className="font-semibold mb-3">Recent enquiries</h3>
        {(data.recent_enquiries || []).length === 0 ? <p className="text-white/40 text-sm">No enquiries yet.</p> :
          <div className="space-y-1 text-sm">{data.recent_enquiries.map((o) => <div key={o.order_number} className="flex justify-between border-b border-white/5 py-1"><span>{o.order_number}</span><span className="text-white/40">{o.status}</span></div>)}</div>}
      </Card>
    </div>
    </div>
  );
}

function Branding({ canEdit }) {
  const [b, setB] = useState(null);
  useEffect(() => { tenantBranding().then((d) => setB({ name: d.name, logo_url: d.logo_url || "", ...(d.brand_colours || {}), website_url: d.contact?.website_url || "", phone: d.contact?.phone || "", email: d.contact?.email || "" })); }, []);
  if (!b) return <p className="text-white/40">Loading…</p>;
  const set = (k, v) => setB((s) => ({ ...s, [k]: v }));
  const save = async (e) => { e.preventDefault(); try { await updateBranding(b); toast.success("Branding saved"); } catch (er) { toast.error(er?.response?.data?.detail || "Could not save (permission?)"); } };
  return (
    <Card className="max-w-2xl" >
      <form onSubmit={save} data-testid="branding-form">
        <Field label="Company name" value={b.name} onChange={(e) => set("name", e.target.value)} disabled={!canEdit} data-testid="branding-name" />
        <Field label="Logo URL" value={b.logo_url} onChange={(e) => set("logo_url", e.target.value)} disabled={!canEdit} data-testid="branding-logo" />
        <div className="grid grid-cols-3 gap-2 mb-3">
          {["primary", "secondary", "accent"].map((k) => (<div key={k}><Label>{k}</Label><Input type="color" value={b[k] || "#000000"} onChange={(e) => set(k, e.target.value)} disabled={!canEdit} data-testid={`branding-${k}`} className="h-9 p-1" /></div>))}
        </div>
        <Field label="Website" value={b.website_url} onChange={(e) => set("website_url", e.target.value)} disabled={!canEdit} data-testid="branding-website" />
        <Field label="Contact phone" value={b.phone} onChange={(e) => set("phone", e.target.value)} disabled={!canEdit} data-testid="branding-phone" />
        <Field label="Contact email" value={b.email} onChange={(e) => set("email", e.target.value)} disabled={!canEdit} data-testid="branding-email" />
        {canEdit ? <Btn type="submit" data-testid="branding-save">Save branding</Btn> : <p className="text-xs text-white/40">Staff can view but not edit branding.</p>}
      </form>
    </Card>
  );
}

function Team({ canManage }) {
  const [members, setMembers] = useState([]);
  const [email, setEmail] = useState(""); const [role, setRole] = useState("staff"); const [inviteUrl, setInviteUrl] = useState("");
  const load = () => listMembers().then(setMembers).catch(() => {});
  useEffect(() => { load(); }, []);
  const invite = async (e) => { e.preventDefault(); try { const r = await teamInvite(email, role); setInviteUrl(r.invite_url); setEmail(""); toast.success("Invitation sent"); load(); } catch (er) { toast.error(er?.response?.data?.detail || "Could not invite"); } };
  const remove = async (uid) => { try { await removeMember(uid); toast.success("Member removed"); load(); } catch (er) { toast.error(er?.response?.data?.detail || "Could not remove"); } };
  return (
    <div className="grid md:grid-cols-2 gap-6">
      <Card><h3 className="font-semibold mb-3">Members ({members.length})</h3>
        <div className="space-y-2" data-testid="member-list">
          {members.map((m) => (<div key={m.user_id} className="flex justify-between items-center bg-[#0a0a0a] border border-white/10 rounded px-3 py-2 text-sm">
            <div><div>{m.name || m.email}</div><div className="text-xs text-white/40">{m.email} · {m.tenant_role}</div></div>
            {canManage && m.tenant_role !== "owner" && <button onClick={() => remove(m.user_id)} className="text-red-400 text-xs hover:underline" data-testid={`remove-${m.user_id}`}>Remove</button>}
          </div>))}
        </div>
      </Card>
      {canManage && (
        <Card><h3 className="font-semibold mb-3">Invite a team member</h3>
          <form onSubmit={invite} data-testid="invite-member-form">
            <Field label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="invite-member-email" required />
            <Label>Role</Label>
            <select value={role} onChange={(e) => setRole(e.target.value)} data-testid="invite-member-role" className="w-full bg-[#0a0a0a] border border-white/15 rounded px-3 py-2 text-sm mb-3">
              <option value="staff">Staff</option><option value="admin">Admin</option><option value="owner">Owner</option>
            </select>
            <Btn type="submit" data-testid="invite-member-submit" className="w-full">Send invitation</Btn>
          </form>
          {inviteUrl && <div className="mt-3 text-xs break-all text-[#00D26A]" data-testid="member-invite-url">{inviteUrl}</div>}
        </Card>
      )}
    </div>
  );
}

function Billing({ canManage }) {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState("");
  const [interval, setInterval] = useState("yearly");
  const load = () => billingSummary().then(setD).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const sub = d?.subscription || {};
    if (!sub.subscription_id && !sub.plan) return;
    const cancelled = sub.status === "canceled" || sub.cancel_at_period_end === true;
    if (!cancelled) return;
    const key = `atd_cancel_tracked_${sub.subscription_id || sub.plan}_${sub.renewal_date || ""}`;
    if (localStorage.getItem(key)) return;
    localStorage.setItem(key, "1");
    track("subscription_cancelled", { plan: sub.plan, cancel_at_period_end: !!sub.cancel_at_period_end, status: sub.status });
  }, [d]);
  if (!d) return <p className="text-white/40">Loading…</p>;
  const ent = d.entitlement || {}; const sub = d.subscription || {};
  const cur = d.current_plan; const money = (n) => `£${Number(n || 0).toFixed(2)}`;
  const yearly = interval === "yearly";

  const go = async (fn) => { setBusy("x"); try { const r = await fn(); if (r?.checkout_url) { track("checkout_started", {}); window.location.href = r.checkout_url; return; } if (r?.url) { window.location.href = r.url; return; } if (r?.mode === "scheduled") toast.success("Downgrade scheduled for your next billing period"); else if (r?.mode === "updated") toast.success("Plan updated"); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Something went wrong"); } finally { setBusy(""); } };

  if (d.is_unlimited) return (
    <Card className="max-w-xl" data-testid="billing-unlimited"><h3 className="font-semibold mb-2">Managed plan</h3>
      <p className="text-white/60 text-sm">This account is on an unlimited managed plan with no billing limits or AI restrictions.</p></Card>
  );

  const priceOf = (p) => yearly ? Number(p.price_gbp_yearly || 0) : Number(p.price_gbp || 0);
  const planBtn = (p) => {
    const isCurrent = sub.plan === p.plan_id && sub.interval === interval && ent.active;
    if (isCurrent) return <span className="text-xs text-[#00D26A] font-semibold" data-testid={`plan-current-${p.plan_id}`}>Current plan</span>;
    const label = !d.can_manage ? "Subscribe" : (priceOf(p) >= (yearly ? Number(cur?.price_gbp_yearly || 0) : Number(cur?.price_gbp || 0)) ? "Upgrade" : "Downgrade");
    return <Btn disabled={!canManage || busy} onClick={() => go(() => billingSubscribe(p.plan_id, interval))} data-testid={`plan-choose-${p.plan_id}`} className="text-xs w-full">{label}</Btn>;
  };

  return (
    <div className="space-y-6" data-testid="billing-tab">
      <div className="grid md:grid-cols-4 gap-4">
        <Card><div className="text-white/40 text-xs uppercase">Current plan</div><div className="text-lg font-semibold mt-1 capitalize">{(sub.plan || "None").replace(/_/g, " ")}</div><div className="text-xs text-white/40 mt-1">{sub.status || "not subscribed"}</div></Card>
        <Card><div className="text-white/40 text-xs uppercase">AI this period</div><div className="text-lg font-semibold mt-1">{ent.used} / {ent.limit}</div><div className="text-xs text-white/40 mt-1">monthly allowance</div></Card>
        <Card><div className="text-white/40 text-xs uppercase">Bonus credits</div><div className="text-lg font-semibold mt-1" data-testid="billing-bonus">{ent.bonus || 0}</div><div className="text-xs text-white/40 mt-1">never expire</div></Card>
        <Card><div className="text-white/40 text-xs uppercase">Renews</div><div className="text-lg font-semibold mt-1">{(sub.renewal_date || "").slice(0, 10) || "—"}</div>{sub.cancel_at_period_end && <div className="text-xs text-yellow-400 mt-1">cancels at period end</div>}</Card>
      </div>

      {d.can_manage && (
        <div className="flex gap-3">
          <Btn variant="ghost" disabled={!canManage || busy} onClick={() => go(billingPortal)} data-testid="billing-manage-btn">Manage subscription</Btn>
        </div>
      )}

      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold">Plans</h3>
          <div className="flex items-center gap-1 bg-[#0a0a0a] border border-white/15 rounded-full p-1" data-testid="interval-toggle">
            <button onClick={() => setInterval("monthly")} data-testid="interval-monthly" className={`px-3 py-1 text-xs rounded-full ${!yearly ? "bg-[#00D26A] text-black font-semibold" : "text-white/60"}`}>Monthly</button>
            <button onClick={() => setInterval("yearly")} data-testid="interval-yearly" className={`px-3 py-1 text-xs rounded-full ${yearly ? "bg-[#00D26A] text-black font-semibold" : "text-white/60"}`}>Yearly <span className="text-[10px]">· Save ~17%</span></button>
          </div>
        </div>
        <div className="grid md:grid-cols-4 gap-4" data-testid="billing-plans">
          {(d.plans || []).map((p) => (
            <Card key={p.plan_id} className={sub.plan === p.plan_id ? "border-[#00D26A]/50" : ""}>
              <div className="font-semibold">{p.name}</div>
              {yearly ? (
                <>
                  <div className="text-2xl font-bold mt-1">{money(p.price_gbp_yearly)}<span className="text-xs text-white/40 font-normal">/yr</span></div>
                  <div className="text-[11px] text-white/40">≈ {money((p.price_gbp_yearly || 0) / 12)}/mo billed annually</div>
                </>
              ) : (
                <div className="text-2xl font-bold mt-1">{money(p.price_gbp)}<span className="text-xs text-white/40 font-normal">/mo</span></div>
              )}
              <div className="text-xs text-[#00D26A] mt-1">{p.ai_allowance} AI generations/mo</div>
              <ul className="text-xs text-white/50 mt-3 space-y-1 min-h-[76px]">{(p.features || []).map((f, i) => <li key={i}>• {f}</li>)}</ul>
              <div className="mt-3">{planBtn(p)}</div>
            </Card>
          ))}
        </div>
      </div>

      <div>
        <h3 className="font-semibold mb-3">AI credit packs <span className="text-xs text-white/40 font-normal">(one-time, never expire)</span></h3>
        <div className="grid md:grid-cols-3 gap-4" data-testid="billing-packs">
          {(d.packs || []).map((p) => (
            <Card key={p.pack_id}>
              <div className="font-semibold">{p.name}</div>
              <div className="text-xl font-bold mt-1">{money(p.price_gbp)}</div>
              <Btn variant="ghost" disabled={!canManage || busy} onClick={() => go(() => billingBuyCredits(p.pack_id))} data-testid={`buy-${p.pack_id}`} className="text-xs w-full mt-3">Buy credits</Btn>
            </Card>
          ))}
        </div>
      </div>
      {!canManage && <p className="text-xs text-white/40">Only the account owner can change the plan or buy credits.</p>}
    </div>
  );
}

function EmbedSettings({ canEdit }) {
  const [d, setD] = useState(null);
  const [domains, setDomains] = useState("");
  const load = () => tenantEmbed().then((r) => { setD(r); setDomains((r.allowed_domains || []).join(", ")); }).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!d) return <p className="text-white/40">Loading…</p>;
  const save = async (patch) => { try { await updateEmbed(patch); toast.success("Embed settings saved"); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Could not save (permission?)"); } };
  const copy = () => { navigator.clipboard.writeText(d.iframe_snippet); toast.success("Embed code copied"); trackActivation("embed_copied"); };
  return (
    <div className="grid lg:grid-cols-2 gap-6" data-testid="embed-tab">
      <Card>
        <h3 className="font-semibold mb-3">Embed your designer</h3>
        <label className="flex items-center gap-2 text-sm mb-4">
          <input type="checkbox" checked={d.enabled} disabled={!canEdit} onChange={(e) => save({ enabled: e.target.checked })} data-testid="embed-enabled" />
          <span>{d.enabled ? "Embed enabled — your designer is live" : "Embed disabled"}</span>
        </label>
        <Label>Public designer URL</Label>
        <div className="flex gap-2 mb-4">
          <Input value={d.embed_url} readOnly data-testid="embed-url" />
          <Btn variant="ghost" onClick={() => { navigator.clipboard.writeText(d.embed_url); toast.success("URL copied"); }} className="text-xs whitespace-nowrap">Copy URL</Btn>
        </div>
        <Label>Allowed domains <span className="text-white/40">(comma-separated; leave empty to allow all)</span></Label>
        <textarea value={domains} disabled={!canEdit} onChange={(e) => setDomains(e.target.value)} data-testid="embed-domains" className="w-full bg-[#0a0a0a] border border-white/15 rounded px-3 py-2 text-sm h-20 mb-2" placeholder="mygym.co.uk, www.mygym.co.uk" />
        {canEdit && <Btn onClick={() => save({ allowed_domains: domains })} data-testid="embed-domains-save" className="text-xs">Save domains</Btn>}
      </Card>
      <Card>
        <h3 className="font-semibold mb-3">Iframe embed code</h3>
        <pre className="bg-[#0a0a0a] border border-white/10 rounded p-3 text-xs text-white/70 overflow-x-auto whitespace-pre-wrap" data-testid="embed-snippet">{d.iframe_snippet}</pre>
        <Btn onClick={copy} data-testid="embed-copy-btn" className="mt-3 text-xs">Copy iframe code</Btn>
        <div className="mt-5 pt-4 border-t border-white/10">
          <h4 className="font-semibold text-sm mb-2">Lead notifications</h4>
          <label className="flex items-center gap-2 text-sm mb-3">
            <input type="checkbox" checked={d.notifications?.new_lead_email} disabled={!canEdit} onChange={(e) => save({ new_lead_email: e.target.checked })} data-testid="embed-notify-toggle" />
            <span>Email me when a new lead comes in</span>
          </label>
          <Field label="Notification email" value={d.notifications?.notify_email || ""} disabled={!canEdit} onChange={(e) => setD({ ...d, notifications: { ...d.notifications, notify_email: e.target.value } })} onBlur={(e) => canEdit && save({ notify_email: e.target.value })} data-testid="embed-notify-email" />
        </div>
      </Card>
    </div>
  );
}

const LEAD_COLS = { new: "text-blue-400", contacted: "text-yellow-400", quoted: "text-purple-400", won: "text-[#00D26A]", lost: "text-red-400" };
function Leads({ canManage }) {
  const [d, setD] = useState(null);
  const [sel, setSel] = useState(null);
  const [sortKey, setSortKey] = useState("date");
  const load = () => tenantLeads().then(setD).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!d) return <p className="text-white/40">Loading…</p>;
  const setStatus = async (id, status) => { try { await updateLeadStatus(id, status); toast.success("Lead updated"); load(); if (sel?.lead_id === id) setSel({ ...sel, status }); } catch (e) { toast.error("Could not update"); } };
  const money = (n) => (typeof n === "number" ? `£${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}` : "—");
  const areaOf = (l) => (l.area != null ? l.area : (l.dimensions?.length && l.dimensions?.width ? +(l.dimensions.length * l.dimensions.width).toFixed(1) : null));
  const sorted = [...d.leads].sort((a, b) => {
    if (sortKey === "value") return (b.estimated_price || 0) - (a.estimated_price || 0);
    if (sortKey === "area") return (areaOf(b) || 0) - (areaOf(a) || 0);
    if (sortKey === "name") return (a.name || "").localeCompare(b.name || "");
    return (b.created_at || "").localeCompare(a.created_at || "");
  });
  const SortBtn = ({ k, label }) => <button onClick={() => setSortKey(k)} data-testid={`sort-${k}`} className={`px-2 py-1 rounded border text-xs uppercase tracking-wide ${sortKey === k ? "border-pgt-green text-pgt-green" : "border-white/15 text-white/50"}`}>{label}</button>;
  return (
    <div data-testid="leads-tab">
      <div className="flex flex-wrap gap-3 mb-3 text-xs">
        {Object.entries(d.counts || {}).map(([s, c]) => <span key={s} className={`px-2 py-1 rounded border border-white/10 ${LEAD_COLS[s]}`} data-testid={`lead-count-${s}`}>{s}: {c}</span>)}
      </div>
      <div className="flex flex-wrap items-center gap-2 mb-4"><span className="text-white/40 text-xs uppercase mr-1">Sort:</span><SortBtn k="value" label="Highest value" /><SortBtn k="area" label="Largest m²" /><SortBtn k="date" label="Newest" /><SortBtn k="name" label="Name" /></div>
      {d.leads.length === 0 ? <Card><p className="text-white/40 text-sm">No leads yet. Enable your embed and share it to start capturing quote requests.</p></Card> : (
        <Card className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="leads-table">
            <thead><tr className="text-white/40 text-xs uppercase text-left border-b border-white/10"><th className="py-2 pr-3">Name</th><th className="pr-3">Company</th><th className="pr-3">Date</th><th className="pr-3">Dimensions</th><th className="pr-3">Total m²</th><th className="pr-3">Project value</th><th className="pr-3">Status</th></tr></thead>
            <tbody>
              {sorted.map((l) => (
                <tr key={l.lead_id} className="border-b border-white/5 hover:bg-white/5 cursor-pointer" onClick={() => setSel(l)} data-testid={`lead-row-${l.lead_id}`}>
                  <td className="py-2 pr-3 font-semibold">{l.name}<div className="text-white/40 text-xs font-normal">{l.email}</div></td>
                  <td className="pr-3">{l.company || "—"}</td>
                  <td className="pr-3 text-white/40 text-xs">{(l.created_at || "").slice(0, 10)}</td>
                  <td className="pr-3">{l.dimensions?.length ? `${l.dimensions.length}m × ${l.dimensions.width}m` : "—"}</td>
                  <td className="pr-3">{areaOf(l) != null ? `${areaOf(l)} m²` : "—"}</td>
                  <td className="pr-3 font-semibold text-pgt-green" data-testid={`lead-value-${l.lead_id}`}>{money(l.estimated_price)}</td>
                  <td className="pr-3">
                    <select value={l.status} disabled={!canManage} onClick={(e) => e.stopPropagation()} onChange={(e) => setStatus(l.lead_id, e.target.value)} data-testid={`lead-status-${l.lead_id}`} className={`bg-[#0a0a0a] border border-white/15 rounded px-2 py-1 text-xs ${LEAD_COLS[l.status]}`}>
                      {["new", "contacted", "quoted", "won", "lost"].map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
      {sel && (
        <div className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-4" onClick={() => setSel(null)} data-testid="lead-detail">
          <Card className="max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-start mb-3"><h3 className="font-semibold text-lg">{sel.name}</h3><button onClick={() => setSel(null)} className="text-white/40 hover:text-white">✕</button></div>
            {sel.preview_url && <img src={sel.preview_url} alt="design preview" className="w-full rounded border border-white/10 mb-3" data-testid="lead-preview" />}
            <div className="text-sm space-y-1 text-white/70">
              <div>Email: <span className="text-white">{sel.email}</span></div>
              <div>Phone: <span className="text-white">{sel.phone || "—"}</span></div>
              <div>Company: <span className="text-white">{sel.company || "—"}</span></div>
              <div>Size: <span className="text-white">{sel.dimensions?.length ? `${sel.dimensions.length}m × ${sel.dimensions.width}m` : "—"}</span> {areaOf(sel) != null ? `(${areaOf(sel)} m²)` : ""}</div>
              <div>Estimated project value: <span className="text-pgt-green font-semibold">{money(sel.estimated_price)}</span></div>
              <div>Status: <span className={LEAD_COLS[sel.status]}>{sel.status}</span></div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}

function DashInner() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("Overview");
  const [data, setData] = useState(null);
  useEffect(() => { tenantDashboard().then(setData).catch(() => {}); }, []);
  const role = data?.role;
  const canEdit = role === "owner" || role === "admin" || !!user?.platform_role;
  const canManage = role === "owner" || !!user?.platform_role;

  return (
    <Shell>
      <div className="flex justify-between items-center">
        <div><div className="text-[#00D26A] text-xs tracking-[0.3em] uppercase mb-1">{data?.tenant?.name || "Dashboard"}</div><H1>Dashboard</H1></div>
        <div className="flex gap-4 text-sm items-center">
          {user?.platform_role && <Link to="/platform" className="text-white/60 hover:text-[#00D26A]">Platform</Link>}
          <Link to="/account" className="text-white/60 hover:text-[#00D26A]">Account</Link>
          <button onClick={async () => { await logout(); nav("/login"); }} className="text-white/60 hover:text-red-400" data-testid="dash-logout">Sign out</button>
        </div>
      </div>
      <div className="flex gap-2 mt-6 mb-6 border-b border-white/10">
        {TABS.map((t) => <button key={t} onClick={() => setTab(t)} data-testid={`tab-${t.toLowerCase()}`} className={`px-4 py-2 text-sm ${tab === t ? "text-[#00D26A] border-b-2 border-[#00D26A]" : "text-white/50"}`}>{t}</button>)}
      </div>
      {!data ? <p className="text-white/40">Loading…</p> : tab === "Overview" ? <Overview data={data} /> : tab === "Analytics" ? <Analytics /> : tab === "Leads" ? <Leads canManage={canEdit} /> : tab === "Embed" ? <EmbedSettings canEdit={canEdit} /> : tab === "Billing" ? <Billing canManage={canManage} /> : tab === "Branding" ? <Branding canEdit={canEdit} /> : <Team canManage={canManage} />}
    </Shell>
  );
}

export default function TenantDashboard() { return <RequireAuth><DashInner /></RequireAuth>; }
